import { useTranslation } from 'react-i18next';
import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageLayout } from '../../components/PageLayout';
import { PageLoader } from '../../components/PageLoader/PageLoader';
import { Button } from '../../components/Button';
import { Alert } from '../../components/Alert/Alert';
import { Paragraph } from '../../components/Paragraph';
import { Label } from '../../components/Label/Label';
import { ContainerCard } from '../../components/ContainerCard';
import { toast } from 'react-toastify';
import { useHandleTeamsOAuthCallback } from '../../hooks/mutations/useHandleTeamsOAuthCallback';
import { useInstallTeamsChannel } from '../../hooks/mutations/useInstallTeamsChannel';
import { Routes } from '../../enums/Routes';

const TOAST_POSITION = 'bottom-right' as const;
import classes from './TeamsOAuthCallbackPage.module.css';

interface Team {
  id: string;
  displayName: string;
  channels: Channel[];
}

interface Channel {
  id: string;
  displayName: string;
}

const TEAM_ID_KEY = 'teams_oauth_teamId';
const CONTEXT_KEY = 'teams_oauth_context';

export const TeamsOAuthCallbackPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string>('');
  const [selectedChannel, setSelectedChannel] = useState<string>('');
  const [teamsWorkspaceId, setTeamsWorkspaceId] = useState<number | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const hasCalledCallbackRef = useRef(false);

  const teamId = sessionStorage.getItem(TEAM_ID_KEY);
  const context = sessionStorage.getItem(CONTEXT_KEY);
  const isCreateSurvey = context === 'create-survey';
  const backUrl = isCreateSurvey
    ? Routes.CreateSurvey
    : teamId ? `/teams/${teamId}/integration` : '/settings';

  const { mutate: handleCallback, error: callbackError } =
    useHandleTeamsOAuthCallback({
      onSuccess: (data) => {
        setTeams(data.teams);
        setTeamsWorkspaceId(data.teamsWorkspaceId);
        setHasLoaded(true);
      },
      onError: () => {
        setHasLoaded(true);
        toast.error(t('teamsOAuthCallback.couldNotConnect'), {
          position: TOAST_POSITION,
        });
      },
    });
  const { mutate: installChannel, isPending: isInstalling } = useInstallTeamsChannel();

  useEffect(() => {
    if (hasCalledCallbackRef.current) return;

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    if (error) {
      hasCalledCallbackRef.current = true;
      setHasLoaded(true);
      const errorMessage = errorDescription ||
        (error === 'access_denied' ? t('teamsOAuthCallback.canceledConnection') : t('teamsOAuthCallback.couldNotConnect'));
      toast.error(errorMessage, { position: TOAST_POSITION });

      const redirectUrl = backUrl;

      sessionStorage.removeItem(TEAM_ID_KEY);
      sessionStorage.removeItem(CONTEXT_KEY);
      setTimeout(() => navigate(redirectUrl), 2000);
      return;
    }

    if (!code || !state) return;

    hasCalledCallbackRef.current = true;
    handleCallback({ code, state });
  }, []);

  const selectedTeamData = teams.find((t) => t.id === selectedTeam);

  const handleInstall = () => {
    if (!selectedTeam || !selectedChannel || !teamsWorkspaceId || !teamId) return;

    installChannel(
      {
        trustpulseTeamId: Number(teamId),
        teamsTeamId: selectedTeam,
        teamsChannelId: selectedChannel,
        teamsWorkspaceId,
      },
      {
        onSuccess: (data) => {
          sessionStorage.removeItem(TEAM_ID_KEY);
          sessionStorage.removeItem(CONTEXT_KEY);
          toast.success(t('teamsOAuthCallback.channelConnected', { channelName: data.channelName }), { position: TOAST_POSITION });

          navigate(backUrl);
        },
        onError: () => {
          toast.error(t('teamsOAuthCallback.couldNotInstall'), { position: TOAST_POSITION });
        },
      }
    );
  };

  const isLoading = !hasLoaded && !callbackError;

  const loadingContent = (
    <div className={classes.centerContainer}>
      <PageLoader />
      <Paragraph size="lg">{t('teamsOAuthCallback.connectingToTeams')}</Paragraph>
    </div>
  );

  if (isLoading) {
    return <PageLayout>{loadingContent}</PageLayout>;
  }

  if (callbackError) {
    const errorContent = (
      <div className={classes.container}>
        <ContainerCard>
          <ContainerCard.Body>
            <Alert variant="danger" title={t('teamsOAuthCallback.somethingWentWrong')} size="lg">
              <Paragraph size="md">{t('teamsOAuthCallback.couldNotConnect')}</Paragraph>
            </Alert>
            <div className={classes.buttonGroup}>
              <Button variant="primary" onClick={() => navigate(backUrl)}>
                {t('teamsOAuthCallback.back')}
              </Button>
            </div>
          </ContainerCard.Body>
        </ContainerCard>
      </div>
    );

    return <PageLayout>{errorContent}</PageLayout>;
  }


  const settingsContent = (
    <div className={classes.container}>
      <ContainerCard>
        <ContainerCard.Title>{t('teamsOAuthCallback.connectToTeams')}</ContainerCard.Title>
        <ContainerCard.Body>
          <Paragraph size="md" className={classes.intro}>
            {t('teamsOAuthCallback.selectChannel')}
          </Paragraph>

          <div className={classes.formGroup}>
            <Label label={t('teamsOAuthCallback.team')} htmlFor="teamSelect" size="ml" required />
            <select
              id="teamSelect"
              className={classes.select}
              value={selectedTeam}
              onChange={(e) => {
                setSelectedTeam(e.target.value);
                setSelectedChannel('');
              }}
            >
              <option value="">{t('teamsOAuthCallback.selectTeam')}</option>
              {teams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.displayName}
                </option>
              ))}
            </select>
          </div>

          {selectedTeamData && selectedTeamData.channels.length > 0 && (
            <div className={classes.formGroup}>
              <Label label={t('teamsOAuthCallback.channel')} htmlFor="channelSelect" size="ml" required />
              <select
                id="channelSelect"
                className={classes.select}
                value={selectedChannel}
                onChange={(e) => setSelectedChannel(e.target.value)}
              >
                <option value="">{t('teamsOAuthCallback.selectChannelOption')}</option>
                {selectedTeamData.channels.map((channel) => (
                  <option key={channel.id} value={channel.id}>
                    #{channel.displayName}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className={classes.buttonGroup}>
            <Button
              variant="primary"
              onClick={handleInstall}
              disabled={!selectedTeam || !selectedChannel || !teamsWorkspaceId || !teamId}
              loading={isInstalling}
              size="large"
            >
              {t('teamsOAuthCallback.connectButton')}
            </Button>
            <Button variant="secondary" onClick={() => navigate(backUrl)} size="large">
              {t('teamsOAuthCallback.cancel')}
            </Button>
          </div>
        </ContainerCard.Body>
      </ContainerCard>
    </div>
  );


  return <PageLayout>{settingsContent}</PageLayout>;
};
